import React from 'react';
import './SignIn.scss';
import { TwSubmitButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import { TwTitle, TwTitleColor, TwTitleType } from '../../components/TwTitles/TwTitles';
import { ReactComponent as TruckbaseLogo } from '../../assets/truckbase-logo.svg';
import { ReactComponent as TbLogo } from '../../assets/tb-logo.svg';
import TwField, { TwInputTypes } from '../../components/TwFields/TwFields';
import { Formik, Form, FormikHelpers } from 'formik';
import AuthService from '../../providers/Auth/Auth.service';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SignInData } from '../../providers/Auth/Auth.model';
import TwCheckbox from '../../components/TwCheckboxes/TwCheckboxes';
import useAuth from '../../providers/Auth/Auth.provider';
import TwAlert from '../../components/TwAlerts/TwAlerts';
import { addCohereIdentity } from '../../utils/cohereUtils';
import { addLogRocketIdentity } from '../../utils/logRocketUtils';
import usePageTitle from '../../hooks/usePageTitle';
import { useAppDispatch } from '../../redux/hooks';
import { twApi } from '../../redux/api/api';

interface SignInFormValues {
  email: string;
  password: string;
  remember: boolean;
}

const SignIn: React.FC = (): JSX.Element => {
  usePageTitle('Sign in | Truckbase');

  const privacyUrl = 'https://truckbase.s3.amazonaws.com/20240506_TruckBase_Privacy_Policy.html';
  const termsUrl =
    'https://uploads-ssl.webflow.com/64183b5795f52c46334f8d02/642da087a0ad300bee887138_TruckBase%20-%20Terms%20of%20Service.pdf';
  const scheduleDemoUrl = 'https://www.truckbase.com/demo';

  const dispatch = useAppDispatch();
  const { updateAuthData, updateUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialValues: SignInFormValues = {
    email: '',
    password: '',
    remember: false
  };

  const signInSubmit = (values: SignInData, actions: FormikHelpers<SignInFormValues>): void => {
    actions.setStatus(undefined);

    AuthService.signIn(values)
      .then((response) => {
        dispatch(twApi.util.resetApiState());
        updateAuthData(!!response.data.id);
        updateUserData(response.data);
        addCohereIdentity(response.data);
        addLogRocketIdentity(response.data);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const state = location?.state as any;

        state?.urlAfterLogin
          ? navigate(state?.urlAfterLogin, { replace: true })
          : navigate('/', { replace: true });
      })
      .catch((error) => {
        actions.setStatus(error.message);
      });
  };

  return (
    <>
      <Formik
        initialStatus={''}
        initialValues={initialValues}
        onSubmit={(values: SignInFormValues, actions: FormikHelpers<SignInFormValues>): void => {
          if (!values.email || !values.password) return;
          signInSubmit(values, actions);
        }}
      >
        {({ values, handleChange, status }): JSX.Element => (
          <Form>
            <div className="sign-in__wrapper tid-sign-in__wrapper">
              <div className="sign-in__form-section">
                <div className="sign-in__form-logo">
                  <TbLogo />
                </div>

                <TwTitle type={TwTitleType.secondary} text="Sign In" />

                <div className="sign-in__inputs-container">
                  {status && <TwAlert message={status} />}

                  <div className="tid-sign-in__email">
                    <TwField
                      id="email"
                      name="email"
                      value={values.email}
                      label="Email"
                      inputType={TwInputTypes.text}
                      disabled={false}
                      onInputChange={handleChange}
                      error={false}
                    />
                  </div>

                  <div className="tid-sign-in__password">
                    <TwField
                      id="password"
                      name="password"
                      value={values.password}
                      label="Password"
                      inputType={TwInputTypes.password}
                      disabled={false}
                      onInputChange={handleChange}
                      error={false}
                    />
                  </div>

                  <div className="sign-in__action-container">
                    <div className="sign-in__two-column">
                      <div className="sign-in__remember">
                        <TwCheckbox
                          label="Remember me"
                          name="remember"
                          disabled={false}
                          onInputChange={handleChange}
                          value={values.remember}
                        />
                      </div>
                      <div className="sign-in__forgot-password">
                        <Link to="/forgot-password"> Forgot password?</Link>
                      </div>
                    </div>
                    <div className="sign-in__submit-button">
                      <TwSubmitButton
                        label="Sign In"
                        disabled={false}
                        type={TwButtonType.primary}
                        fullwidth
                      />
                    </div>

                    <span className="sign-in__agreement tid-sign-in__agreement">
                      <a href={privacyUrl} target="_blank" rel="noreferrer">
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a href={termsUrl} target="_blank" rel="noreferrer">
                        Terms of Service
                      </a>
                    </span>
                  </div>
                </div>
              </div>

              <div className="sign-in__photo-section">
                <div className="sign-in__logo-container">
                  <TruckbaseLogo className="sign-in__logo" />
                </div>
                <TwTitle type={TwTitleType.primary} text="More trucking." />
                <TwTitle
                  type={TwTitleType.primary}
                  color={TwTitleColor.red}
                  text="Less paperwork."
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default SignIn;
